import React from 'react';
import { AppBar, Toolbar, Typography, Button, Box, Menu, MenuItem, ListItemIcon, ListItemText, Tooltip } from '@mui/material';
import { Link, useLocation } from 'react-router-dom';
import LoginIcon from '@mui/icons-material/Login';
import HomeIcon from '@mui/icons-material/Home';
import AppleIcon from '@mui/icons-material/Apple';
import { UserButton } from '@clerk/clerk-react';
import { alpha, useTheme, keyframes } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { createLogger } from '../utils/logger';
import hoodwiseIcon from '../hoodwise_icon.png';
import periploIcon from '../periplo_icon.png';
import { PERIPLO_URL, PERIPLO_BRAND } from '../constants/periplo';
import { textScale } from '../theme/standards';
import { usePlanner } from '../contexts/PlannerContext';

const gradientShift = keyframes`
  0% {
    background-position: 0% 50%;
  }
  50% {
    background-position: 100% 50%;
  }
  100% {
    background-position: 0% 50%;
  }
`;

type NavigationItem =
  | { label: string; path: string; external: false; icon?: React.ReactNode }
  | { label: string; href: string; external: true };

const hoodwiseNavItem: NavigationItem = {
  label: 'Hoodwise',
  external: true,
  href: 'https://www.hoodwise.net/',
};

/** Public App Store page (not the App Store Connect admin URL). */
const APP_STORE_URL = 'https://apps.apple.com/app/id6761609896';

interface NavigationProps {
  isSignedIn: boolean;
  userName?: string;
  onSignIn?: () => void;
  onContactToggle?: () => void;
}

export const Navigation: React.FC<NavigationProps> = ({
  isSignedIn,
  userName,
  onSignIn,
  onContactToggle
}) => {
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { loading } = usePlanner();
  const [mobileMenuAnchorEl, setMobileMenuAnchorEl] = React.useState<null | HTMLElement>(null);
  const [currentPlanUrl, setCurrentPlanUrl] = React.useState<string | null>(null);
  const handleContact = onContactToggle || (() => log.debug('Contact clicked (fallback)'));
  const isMobileMenuOpen = Boolean(mobileMenuAnchorEl);
  const primaryIconColor = theme.palette.primary.main;
  const iconHalo = alpha(primaryIconColor, 0.12);
  const homeIconSx = React.useMemo(
    () => ({
      fontSize: '1.2rem',
      color: primaryIconColor,
      filter: `drop-shadow(0 4px 10px ${alpha(primaryIconColor, 0.25)})`,
    }),
    [primaryIconColor]
  );

  // Check for active plan and stash it for navigation
  React.useEffect(() => {
    const STASH_KEY = 'tripgen_nav_current_plan';

    const checkForActivePlan = () => {
      // First, check if we're currently on a plan route and stash it
      if (location.pathname.startsWith('/trip/')) {
        // Stash this URL so we can navigate back to it
        try {
          localStorage.setItem(STASH_KEY, location.pathname);
        } catch (error) {
          log.error('Error stashing plan URL', error);
        }
        setCurrentPlanUrl(location.pathname);
        return;
      }

      // Otherwise, check if we have a stashed plan URL
      try {
        const stashedUrl = localStorage.getItem(STASH_KEY);
        if (stashedUrl) {
          setCurrentPlanUrl(stashedUrl);
          return;
        }
      } catch (error) {
        log.error('Error reading stashed plan URL', error);
      }

      // Fallback: check localStorage for a saved plan state
      try {
        const saved = localStorage.getItem('tripgen_planner_state');
        if (saved) {
          const state = JSON.parse(saved);
          if (state.searchId) {
            const url = `/trip/${state.searchId}`;
            localStorage.setItem(STASH_KEY, url);
            setCurrentPlanUrl(url);
            return;
          }

          if (state.travelData?.destination && state.travelData?.start_date && state.travelData?.end_date) {
            const url = '/trip';
            localStorage.setItem(STASH_KEY, url);
            setCurrentPlanUrl(url);
            return;
          }
        }
      } catch (error) {
        log.error('Error checking for active plan', error);
      }

      setCurrentPlanUrl(null);
    };

    checkForActivePlan();

    // Listen for storage changes
    window.addEventListener('storage', checkForActivePlan);

    // Also check periodically while on the page (for changes within the same tab)
    const interval = setInterval(checkForActivePlan, 1000);

    return () => {
      window.removeEventListener('storage', checkForActivePlan);
      clearInterval(interval);
    };
  }, [location.pathname]);

  const navigationItems = React.useMemo<NavigationItem[]>(() => {
    const items: NavigationItem[] = [
      { label: 'Planner', path: '/', external: false, icon: <HomeIcon sx={homeIconSx} /> },
      { label: 'Chat', path: '/chat', external: false },
    ];

    // Show Current Plan button when loading OR when we have a plan URL
    // When loading, always use /trip (new trip being generated), not the cached old URL
    if (currentPlanUrl || loading) {
      const path = loading ? '/trip' : currentPlanUrl;
      items.push({ label: 'Current Plan', path: path || '/trip', external: false });
    }

    items.push(
      { label: 'My Trips', path: '/history', external: false },
      { label: 'Destinations', path: '/destinations', external: false },
      { label: 'FAQ', path: '/faq', external: false },
    );

    items.push(hoodwiseNavItem);
    return items;
  }, [currentPlanUrl, loading, homeIconSx]);

  const mobileMenuItems = React.useMemo(
    () =>
      navigationItems.filter(
        (item) => !(item.external === false && 'path' in item && item.path === '/')
      ),
    [navigationItems]
  );

  // Minimal mobile styles - removed excessive overrides
  React.useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      @media (max-width: 600px) {
        .MuiAppBar-root {
          height: 56px !important;
          min-height: 56px !important;
        }
        
        .MuiToolbar-root {
          height: 56px !important;
          min-height: 56px !important;
          padding: 0 8px !important;
        }
      }
    `;
    document.head.appendChild(style);
    
    return () => {
      document.head.removeChild(style);
    };
  }, []);

  const hoverTint = alpha(theme.palette.primary.main, 0.08);
  const activeTint = alpha(theme.palette.primary.main, 0.16);
  const dividerTint = alpha(theme.palette.common.black, 0.06);
  const hoodwiseBrandColor = '#ff2a5e';
  const hoodwiseLogoUrl = hoodwiseIcon;
  const appStoreButton = (
    <Tooltip title="Get the iOS app" arrow placement="bottom">
      <Button
        component="a"
        href={APP_STORE_URL}
        target="_blank"
        rel="noopener noreferrer"
        aria-label="Get TripGen on the App Store"
        startIcon={<AppleIcon sx={{ fontSize: { xs: '1rem', sm: '1.05rem' } }} />}
        sx={{
          textTransform: 'none',
          fontSize: textScale.support.fontSize,
          fontWeight: 600,
          px: { xs: 0.85, sm: 1.5 },
          py: { xs: 0.5, sm: 0.65 },
          minWidth: { xs: 38, sm: 'auto' },
          minHeight: { xs: 38, sm: 'auto' },
          borderRadius: 999,
          color: theme.palette.primary.main,
          border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
          backgroundColor: alpha(theme.palette.primary.main, 0.08),
          boxShadow: `0 3px 10px ${alpha(theme.palette.primary.main, 0.1)}`,
          transition: 'background-color 0.2s ease, color 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease',
          '& .MuiButton-startIcon': {
            marginRight: { xs: 0, sm: 0.65 },
            color: primaryIconColor,
            backgroundColor: iconHalo,
            borderRadius: '50%',
            padding: '5px',
            transition: 'background-color 0.2s ease, color 0.2s ease',
          },
          '& .app-store-label': {
            display: { xs: 'none', sm: 'inline' },
          },
          '&:hover': {
            backgroundColor: alpha(theme.palette.primary.main, 0.14),
            color: theme.palette.primary.dark,
            borderColor: alpha(theme.palette.primary.main, 0.3),
            boxShadow: `0 5px 14px ${alpha(theme.palette.primary.main, 0.14)}`,
            transform: 'translateY(-1px)',
            '& .MuiButton-startIcon': {
              backgroundColor: alpha(primaryIconColor, 0.2),
              color: theme.palette.primary.dark,
            },
          },
        }}
      >
        <span className="app-store-label">App Store</span>
      </Button>
    </Tooltip>
  );
  const periploButton = (
    <Tooltip title="Open Periplo" arrow placement="bottom">
      <Button
        component="a"
        href={PERIPLO_URL}
        target="_blank"
        rel="noopener noreferrer"
        aria-label="Open Periplo"
        startIcon={
          <Box
            component="img"
            src={periploIcon}
            alt=""
            sx={{ width: 18, height: 18, borderRadius: '24%' }}
          />
        }
        sx={{
          display: { xs: 'none', sm: 'inline-flex' },
          textTransform: 'none',
          fontSize: textScale.support.fontSize,
          fontWeight: 600,
          px: 1.5,
          py: 0.65,
          minHeight: 'auto',
          borderRadius: 999,
          color: PERIPLO_BRAND.main,
          border: `1px solid ${alpha(PERIPLO_BRAND.main, 0.22)}`,
          backgroundColor: alpha(PERIPLO_BRAND.main, 0.08),
          boxShadow: `0 3px 10px ${alpha(PERIPLO_BRAND.main, 0.1)}`,
          transition: 'background-color 0.2s ease, color 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease',
          '& .MuiButton-startIcon': {
            marginRight: 0.65,
          },
          '&:hover': {
            backgroundColor: alpha(PERIPLO_BRAND.main, 0.14),
            color: PERIPLO_BRAND.dark,
            borderColor: alpha(PERIPLO_BRAND.main, 0.32),
            boxShadow: `0 5px 14px ${alpha(PERIPLO_BRAND.main, 0.14)}`,
            transform: 'translateY(-1px)',
          },
        }}
      >
        Periplo
      </Button>
    </Tooltip>
  );

  const contactButton = (
    <Button
      onClick={handleContact}
      sx={{
        display: { xs: 'none', md: 'inline-flex' },
        color: theme.palette.primary.main,
        textTransform: 'none',
        fontSize: textScale.support.fontSize,
        px: { xs: 0.5, sm: 1.5 },
        py: { xs: 0.25, sm: 0.5 },
        borderRadius: '6px',
        minWidth: { xs: '45px', sm: 'auto' },
        minHeight: { xs: '28px', sm: 'auto' },
        '&:hover': {
          backgroundColor: hoverTint
        }
      }}
    >
      Contact
    </Button>
  );

  return (
    <AppBar 
      position="fixed" 
      elevation={0}
      sx={{ 
        backgroundColor: alpha(theme.palette.background.paper, 0.92),
        color: theme.palette.text.primary,
        backdropFilter: 'blur(18px)',
        borderBottom: `1px solid ${dividerTint}`,
        height: { xs: '56px', sm: '64px' },
        zIndex: 1200,
      }}
    >
      <Toolbar sx={{ 
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        px: { xs: 1, sm: 2 },
        minHeight: { xs: '56px', sm: '64px' },
        height: { xs: '56px', sm: '64px' }
      }}>
        <Box sx={{
          display: { xs: 'none', md: 'flex' },
          flex: { xs: '0 1 auto', md: 'none' },
          alignItems: 'center',
          gap: { xs: 1, sm: 2 },
          color: theme.palette.text.primary,
          minWidth: 0
        }}>
          <Box
            component="img"
            src="/favicon.svg"
            alt="TripGen AI Logo"
            sx={{ 
              width: { xs: '1.1rem', sm: '1.5rem' },
              height: { xs: '1.1rem', sm: '1.5rem' },
              flexShrink: 0,
              display: 'block'
            }}
          />
          <Typography 
            component={Link} 
            to="/"
            variant="h6"
            sx={{ 
              color: theme.palette.text.primary,
              fontWeight: 700,
              textDecoration: 'none',
              display: 'block',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              minWidth: 0,
              maxWidth: { xs: '120px', sm: 'none' },
              lineHeight: 1,
              '&:hover': { color: theme.palette.primary.main }
            }}
          >
            TripGen AI
          </Typography>
        </Box>

        <Box
          sx={{
            display: { xs: 'flex', md: 'none' },
            alignItems: 'center',
            flexShrink: 0,
            gap: 1
          }}
        >
          <Box
            component={Link}
            to="/"
            aria-label="TripGen AI home"
            sx={{
              width: 34,
              height: 34,
              borderRadius: 10,
              border: `1px solid ${dividerTint}`,
              backgroundColor: alpha(theme.palette.background.default, 0.65),
              boxShadow: `0 6px 14px ${alpha(theme.palette.common.black, 0.12)}`,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'transform 0.2s ease, box-shadow 0.2s ease',
              flexShrink: 0,
              '&:hover': {
                transform: 'translateY(-1px)',
                boxShadow: `0 8px 18px ${alpha(theme.palette.common.black, 0.16)}`
              }
            }}
          >
            <Box
              component="img"
              src="/favicon.svg"
              alt="TripGen AI Icon"
              sx={{ width: 18, height: 18, display: 'block' }}
            />
          </Box>
          <Button
            component={Link}
            to="/"
            startIcon={<HomeIcon sx={{ ...homeIconSx, fontSize: '1.05rem' }} />}
            sx={{
              textTransform: 'none',
              fontSize: textScale.support.fontSize,
              fontWeight: 600,
              px: 1.25,
              py: 0.5,
              borderRadius: 10,
              color: theme.palette.text.primary,
              border: `1px solid ${dividerTint}`,
              backgroundColor: alpha(theme.palette.background.default, 0.4),
              '&:hover': { backgroundColor: hoverTint },
              '& .MuiButton-startIcon': {
                color: primaryIconColor,
                backgroundColor: iconHalo,
                borderRadius: '50%',
                padding: '4px',
                marginRight: 0.75,
                transition: 'background-color 0.2s ease, color 0.2s ease'
              },
              '&:hover .MuiButton-startIcon': {
                backgroundColor: alpha(primaryIconColor, 0.2),
                color: theme.palette.primary.dark
              }
            }}
          >
            Home
          </Button>
        </Box>

        <Box
          sx={{
            display: { xs: 'none', md: 'flex' },
            gap: 1,
            flex: { xs: '1 1 auto', md: '0 1 auto' },
            width: { xs: '100%', md: 'auto' },
            overflowX: { xs: 'auto', md: 'visible' },
            px: { xs: 0, md: 0 },
            pb: { xs: 0.5, md: 0 },
            ml: { xs: 0, md: 2 },
            mr: { xs: 0.5, md: 0 },
            scrollSnapType: { xs: 'x proximity', md: 'none' },
            '&::-webkit-scrollbar': { display: 'none' }
          }}
        >
          {navigationItems.map((item) => {
            if (item.external) {
              return (
                <Button
                  key={item.href}
                  component="a"
                  href={item.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  startIcon={
                    <Box
                      component="img"
                      src={hoodwiseLogoUrl}
                      alt="Hoodwise"
                      sx={{
                        width: 18,
                        height: 18,
                        borderRadius: 4,
                        display: 'block'
                      }}
                    />
                  }
                  sx={{
                    color: alpha(hoodwiseBrandColor, 0.7),
                    textTransform: 'none',
                    fontSize: textScale.support.fontSize,
                    fontWeight: 500,
                    px: 2,
                    py: 1,
                    borderRadius: 999,
                    border: `1px solid ${alpha(hoodwiseBrandColor, 0.15)}`,
                    backgroundColor: alpha(hoodwiseBrandColor, 0.03),
                    whiteSpace: 'nowrap',
                    minWidth: 'max-content',
                    scrollSnapAlign: { xs: 'start', md: 'none' },
                    '&:hover': {
                      backgroundColor: alpha(hoodwiseBrandColor, 0.08),
                      color: hoodwiseBrandColor,
                    }
                  }}
                >
                  Hoodwise
                </Button>
              );
            }

            const isActive = item.label === 'Current Plan'
              ? location.pathname.startsWith('/trip/')
              : location.pathname === item.path;

            const isCurrentPlanLoading = item.label === 'Current Plan' && loading;

            const activeStyles = isActive
              ? {
                  color: theme.palette.primary.main,
                  backgroundColor: activeTint,
                  border: 'none',
                  boxShadow: `0 8px 20px ${alpha(theme.palette.primary.main, 0.1)}`,
                }
              : {
                  color: theme.palette.text.secondary,
                  backgroundColor: 'transparent',
                  border: '1px solid transparent',
                };

            // Special animated gradient styles for Current Plan button when loading
            const loadingGradientStyles = isCurrentPlanLoading
              ? {
                  background: `linear-gradient(90deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 50%, ${theme.palette.primary.main} 100%)`,
                  backgroundSize: '200% 200%',
                  animation: `${gradientShift} 2s ease-in-out infinite`,
                  color: '#fff',
                  fontWeight: 600,
                  border: 'none',
                  boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.35)}`,
                  '&:hover': {
                    background: `linear-gradient(90deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 50%, ${theme.palette.primary.main} 100%)`,
                    backgroundSize: '200% 200%',
                    animation: `${gradientShift} 2s ease-in-out infinite`,
                    color: '#fff',
                  },
                }
              : {};

            return (
              <Button
                key={item.path}
                component={Link}
                to={item.path}
                sx={{
                  textTransform: 'none',
                  fontSize: textScale.support.fontSize,
                  fontWeight: isActive ? 600 : 500,
                  px: 2,
                  py: 1,
                  borderRadius: 999,
                  whiteSpace: 'nowrap',
                  minWidth: 'max-content',
                  scrollSnapAlign: { xs: 'start', md: 'none' },
                  ...activeStyles,
                  ...loadingGradientStyles,
                  '&:hover': isCurrentPlanLoading ? loadingGradientStyles['&:hover'] : {
                    backgroundColor: hoverTint,
                    color: theme.palette.primary.main,
                  },
                }}
              >
                {item.icon || item.label}
              </Button>
            );
          })}
        </Box>
        
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: { xs: 0.5, sm: 1 },
          flexShrink: 0,
          minHeight: { xs: '40px', sm: '48px' },
          height: '100%',
          maxWidth: { xs: 'none', sm: 'auto' },
          overflow: 'visible',
          flex: { xs: '0 1 auto', sm: 'none' }
        }}>
          {appStoreButton}
          {periploButton}
          <Button
            onClick={(event) => setMobileMenuAnchorEl(event.currentTarget)}
            sx={{
              display: { xs: 'inline-flex', md: 'none' },
              minWidth: '36px',
              width: '40px',
              height: '40px',
              borderRadius: 10,
              fontSize: '1.4rem',
              fontWeight: 700,
              lineHeight: 1,
              color: theme.palette.text.primary,
              border: `1px solid ${dividerTint}`,
              backgroundColor: alpha(theme.palette.background.default, 0.4),
              '&:hover': {
                backgroundColor: hoverTint
              }
            }}
            aria-label="Open navigation menu"
          >
            =
          </Button>
          {isSignedIn ? (
            <>
              {contactButton}
              <Typography 
                variant="body2" 
                sx={{ 
                  display: { xs: 'none', md: 'block' },
                  color: theme.palette.text.secondary,
                  mr: 1,
                  whiteSpace: 'nowrap'
                }}
              >
                Welcome, {userName}!
              </Typography>
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center',
                minWidth: { xs: '28px', sm: '40px' },
                height: { xs: '28px', sm: '40px' },
                '& > div': {
                  display: 'flex !important',
                  alignItems: 'center !important'
                },
                '& .cl-userButtonTrigger': {
                  width: { xs: '28px !important', sm: '40px !important' },
                  height: { xs: '28px !important', sm: '40px !important' },
                  minWidth: { xs: '28px !important', sm: '40px !important' },
                  minHeight: { xs: '28px !important', sm: '40px !important' }
                }
              }}>
                <UserButton />
              </Box>
            </>
          ) : (
            <>
              {contactButton}
              <Button
                variant="contained"
                color="primary"
                onClick={onSignIn}
                sx={{
                  fontWeight: 600,
                  textTransform: 'none',
                  px: { xs: 1, sm: 2.5 },
                  py: { xs: 0.75, sm: 1 },
                  fontSize: textScale.body.fontSize,
                  borderRadius: 999,
                  minWidth: { xs: '40px', sm: 'auto' },
                  minHeight: { xs: '40px', sm: 'auto' },
                  height: { xs: '40px', sm: 'auto' },
                  '& .mobile-icon': {
                    display: { xs: 'block', sm: 'none' }
                  },
                  '& .desktop-text': {
                    display: { xs: 'none', sm: 'block' }
                  }
                }}
              >
                <LoginIcon className="mobile-icon" sx={{ fontSize: '1.25rem' }} />
                <span className="desktop-text">Sign In</span>
              </Button>
            </>
          )}
        </Box>
      </Toolbar>
      <Menu
        anchorEl={mobileMenuAnchorEl}
        open={isMobile && isMobileMenuOpen}
        onClose={() => setMobileMenuAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{
          sx: {
            mt: 1,
            borderRadius: 2,
            boxShadow: `0 12px 30px ${alpha(theme.palette.common.black, 0.16)}`,
            minWidth: 220,
          }
        }}
      >
        <MenuItem
          component="a"
          href={APP_STORE_URL}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => setMobileMenuAnchorEl(null)}
          sx={{ gap: 1 }}
        >
          <ListItemIcon sx={{ minWidth: 32, color: primaryIconColor }}>
            <AppleIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText
            primary="App Store"
            primaryTypographyProps={{
              fontWeight: 600,
              fontSize: textScale.support.fontSize,
              color: theme.palette.primary.main,
            }}
          />
        </MenuItem>
        <MenuItem
          component="a"
          href={PERIPLO_URL}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => setMobileMenuAnchorEl(null)}
          sx={{ gap: 1 }}
        >
          <ListItemIcon sx={{ minWidth: 32 }}>
            <Box
              component="img"
              src={periploIcon}
              alt="Periplo"
              sx={{ width: 20, height: 20, borderRadius: '24%' }}
            />
          </ListItemIcon>
          <ListItemText
            primary="Periplo"
            primaryTypographyProps={{
              fontWeight: 600,
              fontSize: textScale.support.fontSize,
              color: PERIPLO_BRAND.main,
            }}
            secondary="Open in new tab"
          />
        </MenuItem>
        {mobileMenuItems.map((item) => {
          if (item.external) {
            return (
              <MenuItem
                key={item.href}
                component="a"
                href={item.href}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => setMobileMenuAnchorEl(null)}
                sx={{ gap: 1 }}
              >
                <ListItemIcon sx={{ minWidth: 32 }}>
                  <Box
                    component="img"
                    src={hoodwiseIcon}
                    alt="Hoodwise"
                    sx={{ width: 20, height: 20, borderRadius: 4 }}
                  />
                </ListItemIcon>
                <ListItemText
                  primary={item.label}
                  primaryTypographyProps={{ fontWeight: 600, fontSize: textScale.support.fontSize }}
                  secondary="Open in new tab"
                />
              </MenuItem>
            );
          }

          return (
            <MenuItem
              key={item.path}
              component={Link}
              to={item.path}
              onClick={() => setMobileMenuAnchorEl(null)}
              sx={{ gap: 1 }}
            >
              {item.icon && (
                <ListItemIcon sx={{ minWidth: 32, color: primaryIconColor }}>
                  {item.icon}
                </ListItemIcon>
              )}
              <ListItemText
                primary={item.label}
                primaryTypographyProps={{
                  fontWeight: item.label === 'Current Plan' ? 700 : 600,
                  fontSize: textScale.support.fontSize,
                  color: item.label === 'Current Plan' ? theme.palette.primary.main : undefined
                }}
              />
            </MenuItem>
          );
        })}
      </Menu>
    </AppBar>
  );
}; 
const log = createLogger('Navigation');
